import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Edit2, Eye, Link2, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const FinanceBadge = ({ status }) => (
  <Badge variant="outline" className={status === 'linked' ? 'text-green-600 border-green-300' : 'text-amber-600 border-amber-300'}>
    {status === 'linked' ? 'Finance: Linked ✓' : 'Finance: Not Linked ⚠'}
  </Badge>
);

const EMPTY_SPONSOR = { name: '', organization: '', contact: '', country: '', status: 'active', notes: '' };

export const SponsorsTab = ({ refreshSignal }) => {
  const [sponsors, setSponsors] = useState([]);
  const [dialog, setDialog] = useState(null); // {editing?}
  const [form, setForm] = useState(EMPTY_SPONSOR);
  const [profile, setProfile] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/sponsorship-program/sponsors`).then((r) => setSponsors(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load, refreshSignal]);

  const save = async () => {
    try {
      if (dialog?.editing) await axios.put(`${API_URL}/api/sponsorship-program/sponsors/${dialog.editing.id}`, form);
      else await axios.post(`${API_URL}/api/sponsorship-program/sponsors`, form);
      toast.success(dialog?.editing ? 'Sponsor updated' : 'Sponsor added');
      setDialog(null); setForm(EMPTY_SPONSOR); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save sponsor'); }
  };

  const openProfile = async (s) => {
    try { setProfile((await axios.get(`${API_URL}/api/sponsorship-program/sponsors/${s.id}/profile`)).data); }
    catch { toast.error('Failed to load profile'); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div><CardTitle className="font-heading">Sponsors</CardTitle><CardDescription>{sponsors.length} sponsor(s)</CardDescription></div>
        <Button size="sm" onClick={() => { setForm(EMPTY_SPONSOR); setDialog({}); }} data-testid="add-sponsor-btn"><Plus className="h-4 w-4 mr-1" /> Add Sponsor</Button>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Sponsor</TableHead><TableHead>Country</TableHead><TableHead className="text-right">Committed</TableHead><TableHead className="text-right">Received</TableHead><TableHead className="text-right">Allocated</TableHead><TableHead className="text-right">Spent</TableHead><TableHead className="text-right">Balance</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {sponsors.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No sponsors yet</TableCell></TableRow> :
              sponsors.map((s) => (
                <TableRow key={s.id} data-testid={`sponsor-row-${s.id}`}>
                  <TableCell><div className="font-medium">{s.name}</div><div className="text-xs text-muted-foreground">{s.ref_code} {s.organization && `· ${s.organization}`}</div></TableCell>
                  <TableCell className="text-sm">{s.country || '—'}</TableCell>
                  <TableCell className="text-right">{formatCurrency(s.amount_committed)}</TableCell>
                  <TableCell className="text-right text-green-600">{formatCurrency(s.amount_received)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(s.amount_allocated)}</TableCell>
                  <TableCell className="text-right text-red-600">{formatCurrency(s.amount_spent)}</TableCell>
                  <TableCell className="text-right font-bold">{formatCurrency(s.remaining_balance)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => openProfile(s)} data-testid={`sponsor-profile-${s.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                      <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => { setForm({ ...EMPTY_SPONSOR, ...s }); setDialog({ editing: s }); }} data-testid={`edit-sponsor-${s.id}`}><Edit2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </CardContent>

      {dialog && (
        <Dialog open onOpenChange={(o) => !o && setDialog(null)}>
          <DialogContent className="max-w-md" data-testid="sponsor-dialog">
            <DialogHeader><DialogTitle>{dialog.editing ? 'Edit Sponsor' : 'Add Sponsor'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {[['name', 'Sponsor Name *'], ['organization', 'Organization'], ['contact', 'Contact'], ['country', 'Country'], ['notes', 'Notes']].map(([k, l]) => (
                <div key={k} className="space-y-1"><Label>{l}</Label>
                  <Input value={form[k] || ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`sponsor-${k}`} /></div>
              ))}
              <div className="space-y-1"><Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                </Select></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
              <Button onClick={save} disabled={!form.name.trim()} data-testid="sponsor-save">{dialog.editing ? 'Update' : 'Add Sponsor'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {profile && (
        <Dialog open onOpenChange={(o) => !o && setProfile(null)}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="sponsor-profile-dialog">
            <DialogHeader>
              <DialogTitle>{profile.sponsor.name}</DialogTitle>
              <DialogDescription>
                Committed {formatCurrency(profile.sponsor.amount_committed)} · Received {formatCurrency(profile.sponsor.amount_received)} · Spent {formatCurrency(profile.sponsor.amount_spent)} · Balance <strong>{formatCurrency(profile.sponsor.remaining_balance)}</strong>
              </DialogDescription>
            </DialogHeader>
            <h4 className="font-medium text-sm">Sponsored Children</h4>
            <Table>
              <TableHeader><TableRow><TableHead>Child</TableHead><TableHead className="text-right">Allocated</TableHead><TableHead className="text-right">Spent</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
              <TableBody>
                {profile.children.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-4 text-muted-foreground">No allocations yet</TableCell></TableRow> :
                  profile.children.map((c) => (
                    <TableRow key={c.child_id}><TableCell>{c.child_name}</TableCell>
                      <TableCell className="text-right">{formatCurrency(c.allocated)}</TableCell>
                      <TableCell className="text-right text-red-600">{formatCurrency(c.spent)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(c.balance)}</TableCell></TableRow>
                  ))}
              </TableBody>
            </Table>
            <h4 className="font-medium text-sm mt-2">Payment History</h4>
            {profile.payments.length === 0 ? <p className="text-sm text-muted-foreground">No payments yet</p> :
              profile.payments.map((p) => (
                <div key={p.id} className="flex justify-between text-sm border-b py-1.5">
                  <span>{formatDate(p.date)} · {p.period || '—'} · {p.allocations?.length || 0} children</span>
                  <span className="font-medium text-green-600">{formatCurrency(p.amount)}</span>
                </div>
              ))}
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

export const DonationsTab = ({ children, onChanged }) => {
  const [donations, setDonations] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ sponsor_id: '', amount: '', date: new Date().toISOString().slice(0, 10), payment_method: '', reference_number: '', period: '', description: '' });
  const [allocs, setAllocs] = useState([]); // {child_id, amount}

  const load = useCallback(() => {
    axios.get(`${API_URL}/api/sponsorship-program/donations`).then((r) => setDonations(r.data)).catch(() => {});
    axios.get(`${API_URL}/api/sponsorship-program/sponsors`).then((r) => setSponsors(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const allocTotal = allocs.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0);
  const amount = parseFloat(form.amount) || 0;

  const equalSplit = () => {
    if (!allocs.length || !amount) return;
    const each = Math.floor(amount / allocs.length);
    setAllocs(allocs.map((a, i) => ({ ...a, amount: i === 0 ? amount - each * (allocs.length - 1) : each })));
  };

  const submit = async () => {
    try {
      await axios.post(`${API_URL}/api/sponsorship-program/donations`, {
        ...form, amount, allocations: allocs.filter((a) => a.child_id).map((a) => ({ child_id: a.child_id, amount: parseFloat(a.amount) || 0 })),
      });
      toast.success('Donation recorded — Finance income created & linked');
      setOpen(false); setAllocs([]); setForm({ ...form, amount: '', reference_number: '', description: '' });
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to record donation'); }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div><CardTitle className="font-heading">Sponsor Donations</CardTitle><CardDescription>Each donation auto-creates a linked Finance income record</CardDescription></div>
        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => setOpen(true)} data-testid="record-donation-btn"><Plus className="h-4 w-4 mr-1" /> Record Donation</Button>
      </CardHeader>
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Sponsor</TableHead><TableHead>Period</TableHead><TableHead className="text-right">Amount</TableHead><TableHead>Children</TableHead><TableHead>Finance</TableHead></TableRow></TableHeader>
          <TableBody>
            {donations.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No donations recorded yet</TableCell></TableRow> :
              donations.map((d) => (
                <TableRow key={d.id} data-testid={`donation-row-${d.id}`}>
                  <TableCell>{formatDate(d.date)}</TableCell>
                  <TableCell className="font-medium">{d.sponsor_name}</TableCell>
                  <TableCell>{d.period || '—'}</TableCell>
                  <TableCell className="text-right font-bold text-green-600">{formatCurrency(d.amount)}</TableCell>
                  <TableCell className="text-xs">{(d.allocations || []).map((a) => `${a.child_name} (${formatCurrency(a.amount)})`).join(', ') || '—'}</TableCell>
                  <TableCell><FinanceBadge status={d.finance_status} /></TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </CardContent>

      {open && (
        <Dialog open onOpenChange={(o) => !o && setOpen(false)}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="donation-dialog">
            <DialogHeader><DialogTitle>Record Donation</DialogTitle>
              <DialogDescription>Allocate the donation across one or more children — allocations must equal the donation amount.</DialogDescription></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1 col-span-2"><Label>Sponsor *</Label>
                <Select value={form.sponsor_id} onValueChange={(v) => setForm({ ...form, sponsor_id: v })}>
                  <SelectTrigger data-testid="donation-sponsor-select"><SelectValue placeholder="Choose sponsor" /></SelectTrigger>
                  <SelectContent>{sponsors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label>Amount (UGX) *</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="donation-amount" /></div>
              <div className="space-y-1"><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
              <div className="space-y-1"><Label>Payment Method</Label>
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{['cash', 'mobile_money', 'bank_transfer', 'cheque', 'other'].map((m) => <SelectItem key={m} value={m}>{m.replace('_', ' ')}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-1"><Label>Reference No.</Label><Input value={form.reference_number} onChange={(e) => setForm({ ...form, reference_number: e.target.value })} /></div>
              <div className="space-y-1"><Label>Sponsorship Period</Label><Input placeholder="e.g. 2026" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} /></div>
              <div className="space-y-1"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <div className="space-y-2 border-t pt-3">
              <div className="flex items-center justify-between">
                <Label>Child Allocations</Label>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => setAllocs([...allocs, { _uid: crypto.randomUUID(), child_id: '', amount: '' }])} data-testid="add-allocation-row">+ Child</Button>
                  <Button size="sm" variant="outline" onClick={equalSplit} data-testid="equal-split-btn">Equal Split</Button>
                </div>
              </div>
              {allocs.map((a, i) => (
                <div key={a._uid} className="flex gap-2">
                  <Select value={a.child_id} onValueChange={(v) => setAllocs(allocs.map((x, j) => j === i ? { ...x, child_id: v } : x))}>
                    <SelectTrigger className="flex-1" data-testid={`alloc-child-${i}`}><SelectValue placeholder="Child" /></SelectTrigger>
                    <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" className="w-36" placeholder="Amount" value={a.amount} onChange={(e) => setAllocs(allocs.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} data-testid={`alloc-amount-${i}`} />
                  <Button size="sm" variant="ghost" onClick={() => setAllocs(allocs.filter((_, j) => j !== i))}>✕</Button>
                </div>
              ))}
              {allocs.length > 0 && (
                <p className={`text-sm font-medium ${Math.abs(allocTotal - amount) < 0.01 ? 'text-green-600' : 'text-red-600'}`} data-testid="alloc-total">
                  Allocated {formatCurrency(allocTotal)} of {formatCurrency(amount)}
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={!form.sponsor_id || !amount || (allocs.length > 0 && Math.abs(allocTotal - amount) > 0.01)} data-testid="donation-submit" className="bg-green-600 hover:bg-green-700">Record Donation</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Card>
  );
};

export const ReportsBar = () => {
  const [key, setKey] = useState('sponsor_report');
  const dl = async (format) => {
    try {
      const res = await axios.get(`${API_URL}/api/sponsorship-program/reports/${key}?format=${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = `${key}.${format === 'excel' ? 'xlsx' : 'pdf'}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Report failed'); }
  };
  return (
    <div className="flex items-center gap-2">
      <Select value={key} onValueChange={setKey}>
        <SelectTrigger className="w-52 h-9" data-testid="sponsorship-report-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {[['sponsor_report', 'Sponsor Report'], ['child_report', 'Child Report'], ['income_report', 'Income Report'], ['expense_report', 'Expense Report'], ['bulk_expense_report', 'Bulk Expense Report']].map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="sm" variant="outline" onClick={() => dl('pdf')} data-testid="sponsorship-report-pdf"><FileDown className="h-3.5 w-3.5 mr-1" />PDF</Button>
      <Button size="sm" variant="outline" onClick={() => dl('excel')} data-testid="sponsorship-report-excel">Excel</Button>
    </div>
  );
};
